import { createTheme } from "@mui/material/styles";

// Resolved values, because MUI computes hover and contrast colours at build time
// and can't read CSS variables. Keep in step with src/styles/tokens.scss.
const BRAND = "#c9320c";
const BRAND_DARK = "#a82a0a";
const NAVY = "#01426a";
const TEXT = "#10212f";
const TEXT_2 = "#43546a";
const BORDER = "#e1e7ee";
const BORDER_STRONG = "#c8d1dc";
const FONT =
  '"Inter Variable", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const base = createTheme();
const phone = base.breakpoints.down("md"); // < 900px; dialogs get cramped before the shell changes.

export const muiTheme = createTheme({
  palette: {
    primary: { main: BRAND, dark: BRAND_DARK, contrastText: "#fff" },
    secondary: { main: NAVY, contrastText: "#fff" },
    error: { main: "#b42318" },
    warning: { main: "#a85606", contrastText: "#fff" },
    success: { main: "#067647" },
    text: { primary: TEXT, secondary: TEXT_2 },
    divider: BORDER,
    background: { default: "#f3f5f8", paper: "#ffffff" },
  },
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: FONT,
    fontSize: 14,
    button: { textTransform: "none", fontWeight: 600 },
    h6: { fontSize: "1rem", fontWeight: 650, letterSpacing: "-0.01em" },
    subtitle2: { fontWeight: 650 },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          [phone]: { minHeight: 44 },
        },
        // Orange is for the one primary action; Cancel and Close stay quiet.
        text: {
          color: TEXT_2,
          "&:hover": { backgroundColor: "#eef2f6" },
        },
        outlined: {
          borderColor: BORDER_STRONG,
          color: TEXT,
          "&:hover": { borderColor: NAVY, backgroundColor: "#eef2f6" },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          [phone]: { minWidth: 44, minHeight: 44 },
        },
      },
    },
    MuiTextField: { defaultProps: { size: "small" } },
    MuiSelect: { defaultProps: { size: "small" } },
    MuiCheckbox: { defaultProps: { color: "primary" } },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: { borderColor: BORDER_STRONG },
      },
    },
    MuiDialog: {
      defaultProps: { scroll: "paper" },
      styleOverrides: {
        // On phones every dialog is a bottom sheet: reachable with a thumb, and
        // the actions stay pinned above the keyboard line.
        container: {
          [phone]: { alignItems: "flex-end" },
        },
        paper: {
          borderRadius: 14,
          boxShadow: "0 12px 32px -8px rgba(16, 33, 47, 0.24)",
          [phone]: {
            margin: 0,
            width: "100%",
            maxWidth: "100% !important",
            maxHeight: "calc(100dvh - 40px)",
            borderRadius: "16px 16px 0 0",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "1.0625rem",
          fontWeight: 650,
          letterSpacing: "-0.01em",
          color: TEXT,
          padding: "14px 16px 14px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          overflowWrap: "anywhere",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: "16px 20px", overflowX: "hidden" },
        dividers: { borderColor: BORDER, padding: "16px 20px" },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "12px 20px",
          gap: 8,
          [phone]: {
            "& > .MuiButton-root": { flex: 1 },
            "& > :not(style) ~ :not(style)": { marginLeft: 0 },
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paperAnchorBottom: {
          borderRadius: "16px 16px 0 0",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 3, borderRadius: "3px 3px 0 0" },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          minHeight: 46,
          color: TEXT_2,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { [phone]: { minHeight: 44 } },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8, overflowWrap: "anywhere" },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderColor: BORDER, borderRadius: 12 },
      },
    },
  },
});
